import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import { CountryCode, CountryName } from 'shared-types'

// Extend dayjs with UTC plugin
dayjs.extend(utc)
dayjs.extend(timezone)

interface ShippingMethod {
  deliveryLabel?: string
  collectionStore?: number
  code?: string
  isAvailable?: boolean
  price?: number
  deliveryCharge?: number
  condition?: string
  isBulkyItem?: boolean
}

interface DeliveryInfo {
  deliveryLabel: string
  deliveryDescription: string
  isFastest: boolean
  code: string
  isAvailable: boolean
  price: number
  condition: string | null
  name: string
  isBulkyItem: boolean
  collectionStore?: number
}

/**
 * Generates delivery information based on the shipping method and current time.
 * @param {ShippingMethod} method - The shipping method object
 * @param {string | undefined} countryCode - The country code (e.g., 'AU', 'NZ')
 * @returns {DeliveryInfo} An object containing delivery label, description, and fastest flag
 */
export const getDeliveryInfo = (
  method: ShippingMethod,
  countryCode?: string
): DeliveryInfo => {
  const STD_DAYS = 5
  const now = dayjs.utc()

  const customerTimezone =
    countryCode?.toLowerCase() !== 'au'
      ? 'Pacific/Auckland'
      : 'Australia/Sydney'
  const nowAEDT = dayjs().tz(customerTimezone)
  const hours = nowAEDT.hour()

  let deliveryLabel = `Standard shipping`
  let isFastest = false
  let deliveryDescription = `Delivery on or before <span class='underline'>${now
    .add(STD_DAYS, 'days')
    .format('ddd, D MMM YYYY')}</span>`

  const minutes = nowAEDT.minute()
  const NEXTDAY_CUTOFF_HOUR = 15 // 3 PM cutoff
  const SAME_DAY_START_HOUR = 0 // 12 AM
  const SAME_DAY_START_MINUTE = 1 // 12:01 AM

  if (method.deliveryLabel === 'Fast') {
    if (
      (hours > SAME_DAY_START_HOUR ||
        (hours === SAME_DAY_START_HOUR && minutes >= SAME_DAY_START_MINUTE)) &&
      hours < NEXTDAY_CUTOFF_HOUR
    ) {
      deliveryLabel = `Same Day Delivery`
      isFastest = true
      deliveryDescription = `Get your items <span class='underline'>Today, order before 3pm.</span>`
    } else {
      deliveryLabel = `Next Day Delivery`
      deliveryDescription = `You just missed our same-day cutoff. Orders placed after <span class='underline'>3pm</span> will ship first thing in the morning.`
    }
  }

  return {
    name: method.deliveryLabel,
    code: method.code,
    isAvailable: method.isAvailable,
    price: method.deliveryCharge || method.price || 0,
    condition: method?.condition || null,
    deliveryLabel,
    deliveryDescription,
    isFastest,
    isBulkyItem: method?.isBulkyItem || false,
    collectionStore: method?.collectionStore,
  }
}

/**
 * Generates delivery information based on the shipping method and current time.
 * @param {ShippingMethod} method - The shipping method object
 * @param {string | undefined} countryCode - The country code (e.g., 'AU', 'NZ')
 * @returns {DeliveryInfo} An object containing delivery label, description, and fastest flag
 */
export const generateDeliveryInfo = (
  method: ShippingMethod,
  countryCode?: string
): DeliveryInfo => {
  const isAu = countryCode === CountryCode.AU

  const customerTimezone = isAu ? 'Australia/Sydney' : 'Pacific/Auckland'
  const currentTime = dayjs().tz(customerTimezone)
  const startOfDay = currentTime.clone().startOf('day')

  const CUTOFF_HOUR = 15 // 3 PM cutoff

  let deliveryLabel = `Standard Delivery`
  let isFastest = false
  let deliveryDescription = `2-5 business days ${CountryName[countryCode]}-wide`

  if (method.deliveryLabel === 'Fast') {
    if (currentTime.isAfter(startOfDay) && currentTime.hour() < CUTOFF_HOUR) {
      deliveryLabel = `3-Hour Express Delivery`
      isFastest = true
      deliveryDescription = `Order before 3pm, get it today`
    } else {
      deliveryLabel = `Next Day Delivery`
      deliveryDescription = `Shipped first thing in the morning`
    }
  }

  return {
    name: method.deliveryLabel,
    code: method.code,
    isAvailable: method.isAvailable,
    price: method.deliveryCharge || method.price || 0,
    condition: method?.condition || null,
    deliveryLabel,
    deliveryDescription,
    isFastest,
    isBulkyItem: method?.isBulkyItem || false,
    collectionStore: method?.collectionStore,
  }
}

// Optional: You can export the types for use elsewhere
export type { ShippingMethod, DeliveryInfo }
